import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { SupportApiService } from "../../Service/SupportApiService";
import Spinner from "../Common/Spinner";
import { CommonService } from "../../Service/CommonService";
import Constant from "../../Constant";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CKEditor from '@ckeditor/ckeditor5-react';
import CloseIcon from "@mui/icons-material/Close";

interface Barrel {
  barrelId: string;
  barrelNumber: string;
  barrelCost: number;
  lotNo: string;
  ttbClass: string;
  ttbType: string;
  proof: number;
  proofGallons: number;
  volume: number;
  description: string;
  fillDate: string;
}

interface Lot {
  productName: string;
  lotNo: string;
  quantity: number;
  destinationBarrelNumberResponse: Barrel[];
}

interface ChildProps {
  customerServiceRequestId: string;
  customerServiceType: string;
  reloadTable?: () => void;
  onClose: () => void;
}

const ManageDestinationBarrelDetails: React.FC<ChildProps> = ({
  customerServiceRequestId,
  customerServiceType,
  onClose,
}) => {
  const [show, setShow] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingData, setIsGettingData] = useState(false);
  const [lots, setLots] = useState<Lot[]>([]);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [editedDestinationBarrelValues, setDestinationBarrelValues] = useState<Record<string, string | number>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyValue, setApplyValue] = useState<number | "">("");

  const handleClose = () => {
    setDestinationBarrelValues({});
    setApplyToAll(false);
    setApplyValue("");
    onClose();
  };

  /* ================= GET DATA ================= */
  const fetchDestinationBarrelDetails = async () => {
    setIsGettingData(true);
    try {
      const response = await SupportApiService.fetchDestinationBarrelDetails(customerServiceRequestId);
      setLots(response.data || []);
      setSelectedLot(response.data?.[0] || null);
    } catch (e) {
      console.error(e);
    } finally {
      setIsGettingData(false);
    }
  };

  // Handle Destination Barrel input per container
  const handleDestinationBarrelDetailsChange = (field: string, value: string | number) => {
    setDestinationBarrelValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const handleSubmit = async () => {
    const errors: Record<string, string> = {};
    const payload: any[] = [];

    const { lotNo, ttbType, ttbClass, proof, proofGallons, description, fillDate } = editedDestinationBarrelValues;
    const plainDescription = String(description || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();

    //if (!lotNo) errors.lotNo = "Lot # required";
    //if (!ttbType) errors.ttbType = "TTB Type required";
    //if (!ttbClass) errors.ttbClass = "TTB Class required";
    //if (!plainDescription) errors.description = "Description required";

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    lots.forEach((lot) => {
      lot.destinationBarrelNumberResponse.forEach((barrel) => {
        payload.push({
          customerServiceRequestId,
          barrelId: barrel.barrelId,
          barrelNumber: barrel.barrelNumber,
          lotNo: lotNo ?? barrel.lotNo,
          ttbType: ttbType ?? barrel.ttbType,
          ttbClass: ttbClass ?? barrel.ttbClass,
          proof: Number(proof ?? barrel.proof),
          proofGallons: Number(proofGallons ?? barrel.proofGallons),
          description: description ?? barrel.description,
          fillDate: fillDate ?? barrel.fillDate,
        });
      });
    });

    if (!payload.length) {
      CommonService.Toast.fire({
        title: "No changes to save",
        icon: "info",
      });
      return;
    }

    setIsLoading(true);
    try {
      await SupportApiService.updateDestinationBarrelDetails(payload);
      CommonService.Toast.fire({
        title: "Destination Container Details Updated",
        icon: "success",
      });
      handleClose();
    } catch (e) {
      CommonService.Toast.fire({
        title: "Update Failed",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchDestinationBarrelDetails();
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="modal__wd modal__wd--submitSellReq"
      size="xl"
      centered
    >
      <Modal.Header className="proofing-modal-header d-flex align-items-center justify-content-between">
        <Modal.Title className="proofing-modal-title">
          Manage Destination Container Details
        </Modal.Title>
        <button className="position-relative bg-transparent p-0 m-0 border-0 " onClick={handleClose}>
          <CloseIcon sx={{ color: "#8F9195" }} />
        </button>
      </Modal.Header>

      <Modal.Body className="pb-2">
        {isGettingData ? (
          <div className="text-center py-4">
            <Spinner />
            <p>Loading Destination Container Details...</p>
          </div>
        ) : (
          <div>
            <div className="list-wrapper"></div>
            {/* Right Panel */}
            {customerServiceType === Constant.CustomerService.RE_CONTAINERIZATION ? (
              <div>
                {selectedLot ? (
                  (() => {
                    const barrel = selectedLot.destinationBarrelNumberResponse[0]; // ONLY CHANGE

                    return (
                      <>
                        <div className="row">
                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              Lot # <span>*</span>
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.lotNo ??
                                  barrel.lotNo ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("lotNo", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                              {validationErrors[`${barrel.barrelId}-lotNo`] && (
                                <span className="error-msg">
                                  {validationErrors[`${barrel.barrelId}-lotNo`]}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>Fill Date:</label>
                            <div className="position-relative">
                              <input
                                type="date"
                                value={
                                  editedDestinationBarrelValues.fillDate ??
                                  barrel.fillDate ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("fillDate", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <div className="text-muted">
                    Select a lot to view container details
                  </div>
                )}
              </div>
            ) : (
              <>
                {selectedLot ? (
                  (() => {
                    const barrel = selectedLot.destinationBarrelNumberResponse[0]; // ONLY CHANGE

                    return (
                      <>
                        <div className="row">
                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              Lot # <span>*</span>
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.lotNo ??
                                  barrel.lotNo ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("lotNo", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                              {validationErrors[`${barrel.barrelId}-lotNo`] && (
                                <span className="error-msg">
                                  {validationErrors[`${barrel.barrelId}-lotNo`]}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              TTB Type <span>*</span>
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.ttbType ??
                                  barrel.ttbType ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("ttbType", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                            </div>
                          </div>

                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              TTB Class <span>*</span>
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.ttbClass ??
                                  barrel.ttbClass ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("ttbClass", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                            </div>
                          </div>

                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              Proof
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.proof ??
                                  barrel.proof ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("proof", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                            </div>
                          </div>

                          <div className="col-sm-6 col-lg-4 detail-input-grp">
                            <label>
                              Proof Gallons
                            </label>
                            <div className="position-relative">
                              <input
                                type="text"
                                value={
                                  editedDestinationBarrelValues.proofGallons ?? barrel.proofGallons ?? 0
                                }
                                onChange={(e) =>
                                  handleDestinationBarrelDetailsChange("proofGallons", e.target.value)
                                }
                                className="proofing-input form-control"
                              />
                            </div>
                          </div>

                          <div className="col-12 detail-input-grp">
                            <label>
                              Description <span>*</span>
                            </label>
                            <div className="position-relative">
                              <CKEditor
                                editor={ClassicEditor}
                                data={
                                  editedDestinationBarrelValues.description ??
                                  barrel.description ??
                                  ""
                                }
                                onChange={(event, editor) =>
                                  handleDestinationBarrelDetailsChange(
                                    "description",
                                    editor.getData()
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <div className="text-muted">
                    Select a lot to view container details
                  </div>
                )}
              </>
            )}

          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-center p-0">
        <div className="btn-group d-flex justify-content-center gap-2 w-100 proofing-modal-footer m-0">
          <button
            style={{ minWidth: "190px" }}
            className="secondary-btn "
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            style={{ minWidth: "190px" }}
            className="primary-btn "
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading && <Spinner />} Add Details
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageDestinationBarrelDetails;
